import moment from 'moment'
import { isArray, padStart, range, replace } from 'lodash'
import i18n from 'simple-react-i18n'
import ApplicationConf from 'conf/ApplicationConf'
import { getAuthorization } from 'utils/ActionUtils'

export const MODEL_AROME = 'arome'
export const MODEL_ARPEGE = 'arpege'
export const REGION_FRANCE = 'france'
export const EPSG_4326 = 'EPSG:4326'
export const EPSG_3857 = 'EPSG:3857'
export const DEFAULT_ANIMATION_SPEED_MS = 450
export const PRELOAD_WINDOW = 16
export const SPEED_OPTIONS = [0.5, 1, 1.5, 2, 3, 4]

export const RADAR_MODELS = {
    [MODEL_AROME]: {
        id: MODEL_AROME,
        modelParam: 'AROME',
        forecastParam: 'prate_snow_arome',
        region: REGION_FRANCE,
        maxEch: 51,
    },
    [MODEL_ARPEGE]: {
        id: MODEL_ARPEGE,
        modelParam: 'ARPEGE',
        forecastParam: 'prate_snow_AROME',
        region: REGION_FRANCE,
        maxEch: 114,
    },
}

export const getRadarModelConfig = (modelKey = MODEL_AROME) => {
    const key = modelKey === MODEL_ARPEGE ? MODEL_ARPEGE : MODEL_AROME
    const baseConfig = RADAR_MODELS[key]
    return {
        ...baseConfig,
        name: key === MODEL_AROME ? i18n.radarAromeName : i18n.radarArpegeName,
        title: key === MODEL_AROME ? i18n.radarAromeTitle : i18n.radarArpegeTitle,
        provider: i18n.radarMeteoFrance,
        description: key === MODEL_AROME ? i18n.radarAromeDesc : i18n.radarArpegeDesc,
    }
}

export const PRECIP_COLORS = [
    { label: '0.1', color: '#9fa8a3' },
    { label: '0.2', color: '#b0c4de' },
    { label: '0.5', color: '#7cb9e8' },
    { label: '1', color: '#2b78d4' },
    { label: '2', color: '#0055b3' },
    { label: '3', color: '#0083a4' },
    { label: '5', color: '#00a846' },
    { label: '7', color: '#44b800' },
    { label: '10', color: '#88b800' },
    { label: '15', color: '#b8b800' },
    { label: '20', color: '#b88000' },
    { label: '30', color: '#b84800' },
    { label: '40', color: '#cc0000' },
    { label: '50', color: '#b30000' },
    { label: '60', color: '#700000' },
    { label: '70', color: '#750055' },
    { label: '80', color: '#8a008a' },
    { label: '100', color: '#9c00d4' },
    { label: '125', color: '#bd00ff' },
]

export const computeFallbackRuns = (modelKey) => {
    const now = moment.utc()
    const runHours = modelKey === MODEL_AROME ? [0, 3, 6, 9, 12, 15, 18, 21] : [0, 6, 12, 18]
    const delayHours = modelKey === MODEL_AROME ? 2.5 : 3.5

    return [1, 0].flatMap((dayOffset) => {
        const targetDay = now.clone().subtract(dayOffset, 'days')
        return runHours
            .map((hour) => {
                const runMoment = targetDay.clone().hour(hour).minute(0).second(0).millisecond(0)
                const availableAt = runMoment.clone().add(delayHours, 'hours')
                return {
                    runStr: runMoment.format('YYYYMMDDHH'),
                    isAvailable: now.isAfter(availableAt) || dayOffset > 0,
                }
            })
            .filter((item) => item.isAvailable)
            .map((item) => item.runStr)
    }).slice(-8)
}

export const generateFallbackFrames = (modelKey, runStr) => {
    const config = RADAR_MODELS[modelKey] || RADAR_MODELS[MODEL_AROME]
    const fallbackRuns = computeFallbackRuns(modelKey)
    const safeRun = runStr || (fallbackRuns.length > 0 ? fallbackRuns[fallbackRuns.length - 1] : moment.utc().format('YYYYMMDDHH'))

    return range(1, config.maxEch + 1).map((forecastHour) => ({
        forecastHour,
        filePath: `/${config.id}/${safeRun}/${config.region}/${config.forecastParam}_${padStart(forecastHour, 3, '0')}.png`,
        runTime: `${safeRun.slice(-2)}z`,
    }))
}

export const fetchRuns = async (modelKey = MODEL_AROME) => {
    const config = RADAR_MODELS[modelKey] || RADAR_MODELS[MODEL_AROME]
    try {
        const response = await fetch(ApplicationConf.pluviometry.radarRuns(encodeURIComponent(config.modelParam)), {
            headers: getAuthorization(),
        })
        if (!response.ok) return computeFallbackRuns(modelKey)
        const runs = await response.json()
        return isArray(runs) && runs.length > 0
            ? [...runs].sort((first, second) => first.localeCompare(second))
            : computeFallbackRuns(modelKey)
    } catch {
        return computeFallbackRuns(modelKey)
    }
}

export const fetchMapFrames = async (modelKey = MODEL_AROME, run = null) => {
    const config = RADAR_MODELS[modelKey] || RADAR_MODELS[MODEL_AROME]
    const runsList = run ? [run] : await fetchRuns(modelKey)
    const targetRun = run || (runsList.length > 0 ? runsList[runsList.length - 1] : null)

    if (!targetRun) {
        return generateFallbackFrames(modelKey, targetRun)
    }

    try {
        const response = await fetch(ApplicationConf.pluviometry.radarMaps(encodeURIComponent(config.modelParam), encodeURIComponent(targetRun)), {
            headers: getAuthorization(),
        })
        if (!response.ok) return generateFallbackFrames(modelKey, targetRun)
        const json = await response.json()
        const rawMaps = isArray(json) ? json : (json?.maps || [])

        const matchedFrames = rawMaps
            .filter((mapItem) => (
                mapItem?.region === config.region &&
                isArray(mapItem?.parameters) &&
                mapItem.parameters.includes(config.forecastParam)
            ))
            .map((frame) => {
                const parsedHour = parseInt(frame.forecast_hour || frame.forecastHour, 10)
                return {
                    forecastHour: parsedHour || 0,
                    filePath: frame.file_path || frame.filePath || '',
                    runTime: frame.run_time || frame.runTime || '',
                }
            })
            .sort((first, second) => first.forecastHour - second.forecastHour)

        return matchedFrames.length > 0 ? matchedFrames : generateFallbackFrames(modelKey, targetRun)
    } catch {
        return generateFallbackFrames(modelKey, targetRun)
    }
}

export const getFullImageUrl = (filePath) => {
    if (!filePath) return ''
    if (filePath.startsWith('data:image')) return filePath
    const cleanPath = filePath.replace(/^\//, '')
    return ApplicationConf.pluviometry.radarImage(cleanPath)
}

export const parseRunMoment = (runStr) => {
    if (!runStr) return moment.utc()
    const cleanStr = replace(runStr, /[^\d]/g, '')
    return cleanStr.length >= 10
        ? moment.utc(cleanStr.slice(0, 10), 'YYYYMMDDHH')
        : moment.utc()
}

export const formatSimulationDate = (runMoment) => {
    const momentUtc = typeof runMoment === 'string' ? parseRunMoment(runMoment) : (runMoment || moment.utc())
    const momentLocal = momentUtc.clone().local()
    const dayAndDate = momentLocal.format('dddd D MMMM YYYY')
    const localHour = momentLocal.format('HH[h]')
    const utcHour = momentUtc.format('HH[h]')
    return `${dayAndDate} ${i18n.radarAt} ${localHour} (${utcHour} ${i18n.radarUtc})`
}

export const formatForecastDate = (runMoment, forecastHour) => {
    const momentUtc = typeof runMoment === 'string' ? parseRunMoment(runMoment) : (runMoment || moment.utc())
    const forecastUtc = momentUtc.clone().add(forecastHour || 0, 'hours')
    const forecastLocal = forecastUtc.clone().local()
    const dayAndDate = forecastLocal.format('dddd D MMMM YYYY')
    const localHour = forecastLocal.format('HH[h]')
    const utcHour = forecastUtc.format('HH[h]')
    return `${dayAndDate} ${i18n.radarAt} ${localHour} (${utcHour} ${i18n.radarUtc})`
}
